import { Link } from "react-router-dom";
import { ShoppingCart, Heart, Crown, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useMemberPricing } from "@/context/MemberPricingContext";
import { formatDA, mediaUrl, imgFallback } from "@/lib/api";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { getMemberPrice } = useMemberPricing();
  const fav = isFavorite(product.id);
  const mp = getMemberPrice(product);
  const promo = product.old_price && product.old_price > product.price;
  const discount = promo ? Math.round((1 - product.price / product.old_price) * 100) : 0;
  const badge = product.badge || (product.is_new ? "NOUVEAU" : null);
  const badgeColor = badge === "NOUVEAU" ? "bg-[var(--mint-600)]" : badge === "COUP DE COEUR" ? "bg-pink-500" : badge === "NATUREL" ? "bg-emerald-500" : "bg-[var(--mint-600)]";

  return (
    <div className="card-boutique group relative p-2.5 flex flex-col" data-testid={`product-card-${product.id}`}>
      {/* Framed image */}
      <Link to={`/produit/${product.id}`} className="block relative rounded-[1.35rem] overflow-hidden bg-mint-50 aspect-square">
        <img src={mediaUrl(product.images?.[0])} alt={product.name} loading="lazy" onError={imgFallback}
          className="w-full h-full object-cover group-hover:scale-[1.07] transition-transform duration-[600ms]" />
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {mp && <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 text-white text-[10px] font-extrabold shadow flex items-center gap-1" data-testid={`product-member-badge-${product.id}`}><Crown className="w-3 h-3" /> PRIX {mp.tier?.toUpperCase()}</span>}
          {promo && !mp && <span className="px-2.5 py-1 rounded-full bg-[var(--mint-600)] text-white text-[12px] font-extrabold shadow-md">-{discount}%</span>}
          {badge && !promo && !mp && <span className={`px-2.5 py-1 rounded-full ${badgeColor} text-white text-[10px] font-extrabold shadow font-mono-label`}>{badge}</span>}
        </div>
        {product.stock <= 0 && <span className="absolute bottom-2.5 left-2.5 px-2.5 py-1 rounded-full bg-slate-800/85 text-white text-[10px] font-semibold">Rupture de stock</span>}
      </Link>

      <button
        onClick={(e) => { e.preventDefault(); toggleFavorite(product); }}
        data-testid={`product-card-fav-${product.id}`}
        aria-label="Ajouter aux favoris"
        className={`absolute top-4 right-4 z-10 w-9 h-9 rounded-full grid place-items-center shadow-md transition-all active:scale-90 ${fav ? "bg-[var(--mint-600)] text-white" : "bg-white/95 text-slate-400 hover:text-[var(--mint-600)]"}`}>
        <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} />
      </button>

      {/* Body */}
      <div className="px-1.5 pt-3.5 pb-1 flex flex-col flex-1">
        {product.brand && <div className="text-[11px] font-mono-label text-[var(--mint-700)] mb-1 truncate">{product.brand}</div>}
        <Link to={`/produit/${product.id}`} className="text-[15px] font-semibold text-slate-800 line-clamp-2 leading-snug hover:text-[var(--mint-700)] transition-colors min-h-[2.6rem]">{product.name}</Link>

        <div className="mt-auto pt-3">
          <div className="flex items-end gap-2 mb-3">
            {mp ? (
              <>
                <div className="font-display font-extrabold text-amber-600 text-xl" data-testid={`product-member-price-${product.id}`}>{formatDA(mp.price)}</div>
                <div className="text-xs text-slate-400 line-through pb-1">{formatDA(mp.original)}</div>
              </>
            ) : (
              <>
                <div className="font-display font-extrabold text-slate-900 text-xl">{formatDA(product.price)}</div>
                {promo && <div className="text-xs text-slate-400 line-through pb-1">{formatDA(product.old_price)}</div>}
              </>
            )}
          </div>
          <button
            onClick={() => addItem(mp ? { ...product, price: mp.price } : product)}
            disabled={product.stock <= 0}
            data-testid={`product-card-add-to-cart-${product.id}`}
            className="btn-coral w-full h-10 flex items-center justify-center gap-2 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
            <ShoppingCart className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}
